import React, { useMemo, useState } from 'react';
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip as UiTooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { fmtNumber } from '@/domain/numbers';
import { formatDateShort, formatDayCompact, hoursBetween, osloTimeOf } from '@/domain/time';
import { COVERAGE_LABELS, DAY_KIND_LABELS } from '@/lib/labels';
import { cn } from '@/lib/utils';
import { Panel, PanelHeader } from './States';

/** Grafpalett avledet av merkevaren: produksjon = oransje, forholdstall/stopp = grafitt/marine, tomgang = blå intensitet, mangler = rød. */
export const CHART = {
  bar: '#F7931D',
  barMuted: 'rgba(247,147,29,0.32)',
  barSelected: '#DC7F12',
  line: '#1D2856',
  stop: '#31313E',
  stopSoft: 'rgba(49,49,62,0.7)',
  idle: 'rgba(35,64,144,0.85)',
  idleSoft: 'rgba(35,64,144,0.35)',
  red: '#B42318',
  grid: 'rgba(31,33,48,0.08)',
  tick: 'rgba(31,33,48,0.62)',
  f1: '#F7931D',
  f2: '#1D2856',
  cursor: 'rgba(31,33,48,0.06)',
};

export function shortDay(date) {
  return formatDayCompact(date);
}

function ChartTooltip({ active, payload, label, rows }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="min-w-[180px] rounded-[var(--hr-radius-sm)] border border-[var(--hr-hairline)] bg-white/95 px-3.5 py-2.5 text-[13px] shadow-[var(--hr-shadow-md)] backdrop-blur">
      <div className="mb-1.5 font-display text-[14px] font-semibold text-[var(--hr-heading)]">{p.date ? formatDateShort(p.date) : label}</div>
      <div className="space-y-1">
        {rows(p).map((r) => (
          <div key={r.label} className="flex items-baseline justify-between gap-4">
            <span className="text-[var(--hr-muted)]">{r.label}</span>
            <span className="hr-num font-semibold text-[var(--hr-text)]">{r.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const axisProps = { tick: { fill: CHART.tick, fontSize: 12, fontFamily: 'Montserrat, Lato, sans-serif' }, axisLine: false, tickLine: false };

function TableToggle({ asTable, onToggle, testId }) {
  return (
    <Button variant="outline" size="sm" className="h-9" onClick={onToggle} data-testid={testId} aria-pressed={asTable}>
      {asTable ? 'Vis som graf' : 'Vis som tabell'}
    </Button>
  );
}

function AxisTitle({ children }) {
  return <div className="mb-1 px-2 text-[12px] font-semibold uppercase tracking-[0.1em] text-[var(--hr-muted-2)]">{children}</div>;
}

/** Liten trendkurve til KPI-fliser og tabeller. Null-verdier bryter linjen. */
export function Sparkline({ values = [], label = 'Trend', width = 88, height = 30, color = '#1D2856', className }) {
  const data = values.map((v, i) => ({ i, v: v === undefined ? null : v }));
  const nums = values.filter((v) => typeof v === 'number');
  if (nums.length < 2) return null;
  const id = `hr-spark-${color.replace(/[^a-zA-Z0-9]/g, '')}`;
  return (
    <div className={cn('shrink-0', className)} style={{ width, height }} aria-label={`${label}: ${nums.length} dagsverdier`} role="img">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 2, right: 1, left: 1, bottom: 2 }}>
          <defs>
            <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.22} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area type="monotone" dataKey="v" stroke={color} strokeWidth={1.5} fill={`url(#${id})`} dot={false} isAnimationActive={false} connectNulls={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Horisontal andelsstolpe (0–100 %) med tall ved siden – farge er aldri eneste bærer. */
export function ShareBar({ value, max = 100, label, className, tone = 'accent', width = 72 }) {
  if (value === null || value === undefined) return <span className="text-[var(--hr-muted)]">–</span>;
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  const bg = tone === 'amber' ? 'var(--hr-amber)' : tone === 'navy' ? 'var(--hr-navy)' : 'var(--hr-accent)';
  return (
    <span className={cn('inline-flex items-center gap-2', className)} aria-label={label}>
      <span className="h-1.5 overflow-hidden rounded-full bg-[var(--hr-inset)]" style={{ width }} aria-hidden="true">
        <span className="block h-full rounded-full" style={{ width: `${pct}%`, background: bg }} />
      </span>
    </span>
  );
}

/**
 * Dominant trendvisning: produksjon (søyler) og ressursintensitet (L/t) på samme, tydelige tidsakse.
 * To justerte grafer med felles markør – ingen dobbelakse.
 */
export function DailyCharts({ days, selectedDates = [], onSelectDay, title = 'Produksjon og diesel per tonn, dag for dag', description }) {
  const [asTable, setAsTable] = useState(false);
  const data = useMemo(
    () =>
      days.map((d) => ({
        ...d,
        label: shortDay(d.date),
        tonnesBar: d.kind === 'planned_off' ? 0 : d.tonnes,
        lpt: d.litresPerTonne,
        selected: selectedDates.includes(d.date),
      })),
    [days, selectedDates],
  );
  const handleClick = (state) => {
    const idx = state?.activeTooltipIndex;
    const label = state?.activeLabel;
    let day = null;
    if (typeof idx === 'number' && data[idx]) day = data[idx];
    else if (label) day = data.find((x) => x.label === label);
    if (day && onSelectDay) onSelectDay(day.date);
  };
  const anySelected = selectedDates.length > 0;
  const windowLabel = data.length ? `${formatDateShort(data[0].date)} – ${formatDateShort(data[data.length - 1].date)}` : '';
  const legend = (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-[var(--hr-muted)]">
      <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-[3px]" style={{ background: CHART.bar }} /> Produsert (t)</span>
      <span className="inline-flex items-center gap-1.5"><span className="inline-block h-[3px] w-4 rounded-full" style={{ background: CHART.line }} /> Diesel per tonn (L/t)</span>
      {anySelected && <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-[3px]" style={{ background: CHART.barSelected }} /> Valgt</span>}
    </div>
  );
  return (
    <Panel data-testid="daily-chart" className="flex h-full flex-col">
      <PanelHeader
        title={title}
        description={description ?? `${windowLabel}. Klikk en dag for å filtrere alle paneler. Driftsfrie dager vises uten søyle; dager uten komplett dieselgrunnlag har ingen L/t-verdi.`}
        actions={<TableToggle asTable={asTable} onToggle={() => setAsTable((s) => !s)} testId="daily-chart-table-toggle" />}
      />
      {asTable ? (
        <div className="hr-scroll overflow-x-auto">
          <Table data-testid="daily-chart-table">
            <TableHeader>
              <TableRow>
                <TableHead>Dato</TableHead>
                <TableHead>Dagtype</TableHead>
                <TableHead className="text-right">Produsert (t)</TableHead>
                <TableHead className="text-right">Diesel (L)</TableHead>
                <TableHead className="text-right">L/t</TableHead>
                <TableHead className="text-right">Produksjonstid (h)</TableHead>
                <TableHead className="text-right">Stopp (h)</TableHead>
                <TableHead>Dekning</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((d) => (
                <TableRow key={d.date} className={cn('cursor-pointer', d.selected && 'hr-row-selected')} onClick={() => onSelectDay?.(d.date)} tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && onSelectDay?.(d.date)} data-testid={`daily-table-row-${d.date}`}>
                  <TableCell className="hr-tabular font-medium">{formatDateShort(d.date)}</TableCell>
                  <TableCell className="text-[var(--hr-muted)]">{DAY_KIND_LABELS[d.kind]}</TableCell>
                  <TableCell className="hr-num text-right">{d.kind === 'planned_off' ? '–' : fmtNumber(d.tonnes, 0)}</TableCell>
                  <TableCell className="hr-num text-right">{d.kind === 'planned_off' ? '–' : `${fmtNumber(d.fuelLitres, 0)}${d.fuelComplete ? '' : ' (delvis)'}`}</TableCell>
                  <TableCell className="hr-num text-right">{d.litresPerTonne === null ? <span className="font-sans text-[var(--hr-amber)]">utilgjengelig</span> : fmtNumber(d.litresPerTonne, 3)}</TableCell>
                  <TableCell className="hr-num text-right">{fmtNumber(d.productionHours, 1)}</TableCell>
                  <TableCell className="hr-num text-right">{fmtNumber(d.stopHours, 1)}</TableCell>
                  <TableCell className="text-[var(--hr-muted)]">{COVERAGE_LABELS[d.coverage]}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="flex flex-1 flex-col px-2 py-4 sm:px-4">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-2" data-testid="daily-chart-legend">
            {legend}
            {windowLabel && <span className="hr-tabular text-[13px] text-[var(--hr-muted-2)]">{windowLabel}</span>}
          </div>
          <AxisTitle>Produsert mengde (t)</AxisTitle>
          <div className="h-[240px] min-h-[200px] flex-1">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} syncId="daily" margin={{ top: 8, right: 12, left: 0, bottom: 0 }} onClick={handleClick} style={{ cursor: onSelectDay ? 'pointer' : 'default' }}>
                <CartesianGrid stroke={CHART.grid} vertical={false} />
                <XAxis dataKey="label" {...axisProps} interval="preserveStartEnd" minTickGap={18} />
                <YAxis {...axisProps} width={52} tickFormatter={(v) => fmtNumber(v, 0)} />
                <Tooltip
                  cursor={{ fill: CHART.cursor }}
                  content={<ChartTooltip rows={(p) => [{ label: 'Produsert', value: p.kind === 'planned_off' ? 'Ingen planlagt produksjon' : `${fmtNumber(p.tonnes, 0)} t` }, { label: 'Produksjonstid', value: `${fmtNumber(p.productionHours, 1)} h` }, { label: 'Stopp', value: `${fmtNumber(p.stopHours, 1)} h` }]} />}
                />
                <Bar dataKey="tonnesBar" radius={[4, 4, 0, 0]} isAnimationActive={false} maxBarSize={40}>
                  {data.map((d) => (
                    <Cell key={d.date} fill={d.selected ? CHART.barSelected : anySelected ? CHART.barMuted : CHART.bar} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-5">
            <AxisTitle>Diesel per tonn (L/t)</AxisTitle>
          </div>
          <div className="h-[170px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data} syncId="daily" margin={{ top: 8, right: 12, left: 0, bottom: 0 }} onClick={handleClick} style={{ cursor: onSelectDay ? 'pointer' : 'default' }}>
                <CartesianGrid stroke={CHART.grid} vertical={false} />
                <XAxis dataKey="label" {...axisProps} interval="preserveStartEnd" minTickGap={18} />
                <YAxis {...axisProps} width={52} domain={[0, 'auto']} tickFormatter={(v) => fmtNumber(v, 2)} />
                <Tooltip content={<ChartTooltip rows={(p) => [{ label: 'L/t', value: p.lpt === null ? 'utilgjengelig' : fmtNumber(p.lpt, 3) }, { label: 'Diesel', value: `${fmtNumber(p.fuelLitres, 0)} L${p.fuelComplete ? '' : ' (delvis)'}` }]} />} />
                <Line type="monotone" dataKey="lpt" stroke={CHART.line} strokeWidth={2.25} dot={{ r: 3, fill: '#fff', stroke: CHART.line, strokeWidth: 2 }} activeDot={{ r: 5.5, fill: CHART.bar, stroke: '#fff', strokeWidth: 2 }} connectNulls={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </Panel>
  );
}

const SHIFT_START = 7;
const SHIFT_END = 16;

function hourFrac(iso, date) {
  const t = osloTimeOf(iso);
  const [h, m] = t.split(':').map(Number);
  let hh = h + m / 60;
  if (hh === 0 && iso > `${date}T12`) hh = 24;
  return hh;
}

/** Tidslinje for et skift (07–16 Oslo) med produksjon/stopp-segmenter og valgfri maskinrad. */
export function ShiftTimeline({ date, lineStates, slots, onSegmentClick, onSlotClick, compact = false, machineLabel }) {
  const span = SHIFT_END - SHIFT_START;
  const pos = (iso) => Math.min(100, Math.max(0, ((hourFrac(iso, date) - SHIFT_START) / span) * 100));
  const states = lineStates.filter((s) => s.state !== 'planned_off');
  const plannedOff = lineStates.some((s) => s.state === 'planned_off');
  const hours = Array.from({ length: span + 1 }, (_, i) => SHIFT_START + i);
  const labelW = machineLabel ? 'w-24' : '';
  return (
    <div className="space-y-1.5" data-testid={`shift-timeline-${date}`}>
      {!compact && (
        <div className="flex items-center gap-3">
          {machineLabel && <span className={cn('shrink-0', labelW)} />}
          <div className="hr-tabular relative h-4 flex-1 text-[11px] text-[var(--hr-muted-2)]">
            {hours.map((h) => (
              <span key={h} className="absolute -translate-x-1/2" style={{ left: `${((h - SHIFT_START) / span) * 100}%` }}>
                {String(h).padStart(2, '0')}
              </span>
            ))}
          </div>
        </div>
      )}
      <div className="flex items-center gap-3">
        {machineLabel && <span className={cn('shrink-0 text-[13px] font-medium text-[var(--hr-muted)]', labelW)}>Knuseverk</span>}
        <div className="relative h-8 flex-1 overflow-hidden rounded-md bg-[var(--hr-inset)] ring-1 ring-inset ring-[var(--hr-hairline)]" role="list" aria-label={`Linjetilstand ${formatDateShort(date)}`}>
          {plannedOff && <div className="flex h-full items-center justify-center text-[13px] text-[var(--hr-muted)]">Ingen planlagt produksjon</div>}
          {states.map((s) => {
            const left = pos(s.fromUtc);
            const width = pos(s.toUtc) - left;
            const isStop = s.state === 'stop';
            const label = `${isStop ? 'Stopp' : 'Produksjon'} ${osloTimeOf(s.fromUtc)}–${osloTimeOf(s.toUtc)} (${fmtNumber(hoursBetween(s.fromUtc, s.toUtc), 2)} h)${s.stopReason ? `: ${s.stopReason}` : ''}`;
            return (
              <UiTooltip key={s.id}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    role="listitem"
                    onClick={() => onSegmentClick?.(s)}
                    aria-label={label}
                    data-testid={`timeline-segment-${s.id}`}
                    className={cn(
                      'absolute top-0 h-full border-r border-white/80 text-[11px] font-semibold transition-[filter] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--hr-focus)]',
                      isStop ? 'hr-stripes text-white' : 'text-[#3A2200]',
                      onSegmentClick ? 'cursor-pointer hover:brightness-110' : 'cursor-default',
                    )}
                    style={{ left: `${left}%`, width: `${width}%`, background: isStop ? CHART.stopSoft : 'rgba(247,147,29,0.8)' }}
                  >
                    {width > 9 && <span className="px-1.5">{isStop ? 'Stopp' : 'Prod.'}</span>}
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">{label}</TooltipContent>
              </UiTooltip>
            );
          })}
        </div>
      </div>
      {slots && (
        <div className="flex items-center gap-3">
          {machineLabel && <span className={cn('shrink-0 truncate text-[13px] font-medium text-[var(--hr-muted)]', labelW)}>{machineLabel}</span>}
          <div className="relative h-8 flex-1 overflow-hidden rounded-md bg-[var(--hr-inset)] ring-1 ring-inset ring-[var(--hr-hairline)]" role="list" aria-label={`Maskinintervaller ${formatDateShort(date)}`}>
            {slots.map((mi) => {
              const left = pos(mi.fromUtc);
              const width = pos(mi.toUtc) - left;
              const idleShare = mi.engineHours && mi.idleHours !== null ? mi.idleHours / mi.engineHours : null;
              const missing = mi.fuelLitres === null || mi.engineHours === null;
              const label = `${osloTimeOf(mi.fromUtc)}–${osloTimeOf(mi.toUtc)}: motor ${mi.engineHours === null ? 'mangler' : fmtNumber(mi.engineHours, 2) + ' h'}, tomgang ${mi.idleHours === null ? 'mangler' : fmtNumber(mi.idleHours, 2) + ' h'}${idleShare !== null ? ` (${fmtNumber(idleShare * 100, 0)} %)` : ''}, diesel ${mi.fuelLitres === null ? 'mangler' : fmtNumber(mi.fuelLitres, 1) + ' L'}`;
              const alpha = idleShare === null ? 0.12 : 0.15 + Math.min(0.8, idleShare) * 0.95;
              return (
                <UiTooltip key={mi.id}>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      role="listitem"
                      onClick={() => onSlotClick?.(mi)}
                      aria-label={label}
                      data-testid={`timeline-slot-${mi.id}`}
                      className={cn('absolute top-0 h-full border-r border-white/80 text-[11px] font-semibold transition-[filter] duration-150 hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--hr-focus)]', missing && 'hr-stripes-red')}
                      style={{ left: `${left}%`, width: `${width}%`, background: missing ? 'rgba(180,35,24,0.22)' : `rgba(35,64,144,${alpha})` }}
                    >
                      {width > 8 && idleShare !== null && <span className={cn('px-1', alpha > 0.55 ? 'text-white' : 'text-[var(--hr-text)]')}>{fmtNumber(idleShare * 100, 0)} %</span>}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">{label}</TooltipContent>
                </UiTooltip>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export function TimelineLegend({ withMachine = false }) {
  return (
    <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-[13px] text-[var(--hr-muted)]">
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block h-3 w-5 rounded-sm" style={{ background: 'rgba(247,147,29,0.8)' }} /> Produksjon
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="hr-stripes inline-block h-3 w-5 rounded-sm" style={{ background: CHART.stopSoft }} /> Stopp i knuseverket
      </span>
      {withMachine && (
        <>
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block h-3 w-5 rounded-sm" style={{ background: 'linear-gradient(90deg, rgba(35,64,144,0.2), rgba(35,64,144,0.9))' }} /> Tomgangsandel per time (mørkere = mer tomgang)
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="hr-stripes-red inline-block h-3 w-5 rounded-sm" style={{ background: 'rgba(180,35,24,0.22)' }} /> Manglende måling
          </span>
        </>
      )}
    </div>
  );
}

/** Fraksjoner som stablede søyler med totalsum. */
export function FractionChart({ days, fractions }) {
  const [asTable, setAsTable] = useState(false);
  const data = days.map((d) => ({ date: d.date, label: shortDay(d.date), ...Object.fromEntries(fractions.map((f) => [f.fractionId, d.tonnesByFraction[f.fractionId] ?? 0])), total: d.tonnes, kind: d.kind }));
  return (
    <Panel data-testid="fraction-chart">
      <PanelHeader
        title="Hvordan fordeler produksjonen seg på fraksjoner?"
        description="Stablede søyler per dag med totalsum. Linjetid telles én gang uavhengig av fraksjoner."
        actions={<TableToggle asTable={asTable} onToggle={() => setAsTable((s) => !s)} testId="fraction-chart-table-toggle" />}
      />
      {asTable ? (
        <div className="hr-scroll overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Dato</TableHead>
                {fractions.map((f) => (
                  <TableHead key={f.fractionId} className="text-right">
                    {f.name} (t)
                  </TableHead>
                ))}
                <TableHead className="text-right">Totalt (t)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((d) => (
                <TableRow key={d.date}>
                  <TableCell className="hr-tabular font-medium">{formatDateShort(d.date)}</TableCell>
                  {fractions.map((f) => (
                    <TableCell key={f.fractionId} className="hr-num text-right">
                      {fmtNumber(d[f.fractionId], 0)}
                    </TableCell>
                  ))}
                  <TableCell className="hr-num text-right font-semibold">{fmtNumber(d.total, 0)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="px-3 py-4 sm:px-5">
          <div className="h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} margin={{ top: 4, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid stroke={CHART.grid} vertical={false} />
                <XAxis dataKey="label" {...axisProps} />
                <YAxis {...axisProps} width={56} tickFormatter={(v) => fmtNumber(v, 0)} />
                <Tooltip cursor={{ fill: CHART.cursor }} content={<ChartTooltip rows={(p) => [...fractions.map((f) => ({ label: f.name, value: `${fmtNumber(p[f.fractionId], 0)} t` })), { label: 'Totalt', value: `${fmtNumber(p.total, 0)} t` }]} />} />
                {fractions.map((f, i) => (
                  <Bar key={f.fractionId} dataKey={f.fractionId} stackId="a" fill={i === 0 ? CHART.f1 : CHART.f2} isAnimationActive={false} maxBarSize={44} radius={i === fractions.length - 1 ? [4, 4, 0, 0] : 0} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 flex flex-wrap gap-5 px-2 text-[13px] text-[var(--hr-muted)]">
            {fractions.map((f, i) => (
              <span key={f.fractionId} className="inline-flex items-center gap-1.5">
                <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: i === 0 ? CHART.f1 : CHART.f2 }} /> {f.name} ({fmtNumber(f.share * 100, 0)} %)
              </span>
            ))}
          </div>
        </div>
      )}
    </Panel>
  );
}

/** Trend for én maskin: diesel per dag (søyler) og tomgangsandel (linje), justerte grafer. */
export function MachineTrendChart({ days, selectedDates = [], onSelectDay }) {
  const [asTable, setAsTable] = useState(false);
  const data = days.map((d) => ({ ...d, label: shortDay(d.date), selected: selectedDates.includes(d.date) }));
  const handleClick = (state) => {
    const idx = state?.activeTooltipIndex;
    if (typeof idx === 'number' && data[idx] && onSelectDay) onSelectDay(data[idx].date);
  };
  return (
    <Panel data-testid="machine-trend-chart">
      <PanelHeader
        title="Hvordan har diesel og tomgang utviklet seg for maskinen?"
        description="Dieselforbruk per dag og tomgangsandel av egne motortimer. Klikk en dag for å filtrere."
        actions={<TableToggle asTable={asTable} onToggle={() => setAsTable((s) => !s)} testId="machine-trend-table-toggle" />}
      />
      {asTable ? (
        <div className="hr-scroll overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Dato</TableHead>
                <TableHead className="text-right">Motortimer (h)</TableHead>
                <TableHead className="text-right">Tomgang (h)</TableHead>
                <TableHead className="text-right">Tomgangsandel (%)</TableHead>
                <TableHead className="text-right">Diesel (L)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((d) => (
                <TableRow key={d.date}>
                  <TableCell className="hr-tabular font-medium">{formatDateShort(d.date)}</TableCell>
                  <TableCell className="hr-num text-right">{fmtNumber(d.engineHours, 2)}</TableCell>
                  <TableCell className="hr-num text-right">{fmtNumber(d.idleHours, 2)}</TableCell>
                  <TableCell className="hr-num text-right">{d.idleShare === null ? '–' : fmtNumber(d.idleShare, 1)}</TableCell>
                  <TableCell className="hr-num text-right">{d.fuelComplete ? fmtNumber(d.fuelLitres, 1) : <span className="text-[var(--hr-amber)]">{fmtNumber(d.fuelLitres, 1)} (delvis)</span>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="px-3 py-4 sm:px-5">
          <AxisTitle>Diesel (L)</AxisTitle>
          <div className="h-[180px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} syncId="machine" margin={{ top: 4, right: 12, left: 0, bottom: 0 }} onClick={handleClick} style={{ cursor: 'pointer' }}>
                <CartesianGrid stroke={CHART.grid} vertical={false} />
                <XAxis dataKey="label" {...axisProps} interval="preserveStartEnd" />
                <YAxis {...axisProps} width={48} tickFormatter={(v) => fmtNumber(v, 0)} />
                <Tooltip cursor={{ fill: CHART.cursor }} content={<ChartTooltip rows={(p) => [{ label: 'Diesel', value: `${fmtNumber(p.fuelLitres, 1)} L${p.fuelComplete ? '' : ' (delvis)'}` }, { label: 'Motortimer', value: `${fmtNumber(p.engineHours, 2)} h` }]} />} />
                <Bar dataKey="fuelLitres" radius={[4, 4, 0, 0]} isAnimationActive={false} maxBarSize={44}>
                  {data.map((d) => (
                    <Cell key={d.date} fill={!d.fuelComplete ? 'rgba(154,91,0,0.35)' : d.selected ? CHART.barSelected : selectedDates.length ? CHART.barMuted : CHART.bar} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4">
            <AxisTitle>Tomgangsandel (%)</AxisTitle>
          </div>
          <div className="h-[140px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data} syncId="machine" margin={{ top: 6, right: 12, left: 0, bottom: 0 }} onClick={handleClick} style={{ cursor: 'pointer' }}>
                <CartesianGrid stroke={CHART.grid} vertical={false} />
                <XAxis dataKey="label" {...axisProps} interval="preserveStartEnd" />
                <YAxis {...axisProps} width={48} domain={[0, 'auto']} tickFormatter={(v) => fmtNumber(v, 0)} />
                <Tooltip content={<ChartTooltip rows={(p) => [{ label: 'Tomgangsandel', value: p.idleShare === null ? '–' : `${fmtNumber(p.idleShare, 1)} %` }, { label: 'Tomgang', value: `${fmtNumber(p.idleHours, 2)} h` }]} />} />
                <Line type="monotone" dataKey="idleShare" stroke={CHART.idle} strokeWidth={2} dot={{ r: 2.5, fill: CHART.idle, strokeWidth: 0 }} activeDot={{ r: 5, fill: CHART.bar, stroke: '#fff', strokeWidth: 2 }} connectNulls={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </Panel>
  );
}
